<template>
  <div class="anamnesis-subset">
    <SubsetToolbar
      :sort-options="sortOptions"
      :sort-by="sortBy"
      :sort-direction="sortDirection"
      :is-card-view="isCardView"
      :search-query="searchQuery"
      search-placeholder="Rechercher une collection..."
      create-button-text="Nouvelle collection"
      :show-create-button="true"
      @update:sort-by="sortBy = $event"
      @update:search-query="searchQuery = $event"
      @toggle-sort-direction="toggleSortDirection"
      @toggle-view="toggleView"
      @create="$emit('create')"
    />

    <div v-if="isLoading" class="text-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto"></div>
      <p class="text-gray-500 mt-2">Chargement des collections...</p>
    </div>
    
    <div v-else-if="paginatedCollections.length > 0" :class="{ 'anamnesis-grid': isCardView, 'anamnesis-table': !isCardView }">
      <CollectionCard
        v-for="collection in paginatedCollections"
        :key="collection.uuid"
        :collection="collection"
        :view-mode="isCardView ? 'card' : 'table'"
        :selectable="false"
        @click="$emit('view', collection)"
        @edit="() => $emit('edit', collection)"
        @delete="$emit('delete', collection)"
      />
    </div>

    <div v-else class="text-center py-8">
      <p class="text-gray-500">Aucune collection trouvée.</p>
    </div>

    <div v-if="filteredCollections.length > itemsPerPage" class="mt-4 flex justify-center space-x-2">
      <!-- Pagination Controls -->
    </div>
  </div>
</template>

<script setup lang="ts">
import type { MemoCollection } from '@/api/types/memos/collections';
import CollectionCard from '@/components/cards/CollectionCard.vue';
import type { SortOption } from '@/components/UI/SubsetToolbar.vue';
import SubsetToolbar from '@/components/UI/SubsetToolbar.vue';
import type { PropType } from 'vue';
import { computed, ref } from 'vue';

const props = defineProps({
  items: {
    type: Array as PropType<MemoCollection[]>,
    required: true,
  },
  itemsPerPage: {
    type: Number,
    default: 12,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

defineEmits<{
  (e: 'create'): void;
  (e: 'view', collection: MemoCollection): void;
  (e: 'edit', collection: MemoCollection): void;
  (e: 'delete', collection: MemoCollection): void;
}>();

const sortBy = ref('name');
const sortDirection = ref<'asc' | 'desc'>('asc');
const searchQuery = ref('');
const currentPage = ref(1);
const isCardView = ref(true); // Support both card and table views

const sortOptions: SortOption[] = [
  { value: 'name', label: 'Nom' },
  { value: 'created_at', label: 'Date de création' },
  { value: 'memos_count', label: 'Nombre de mémos' },
];

const toggleSortDirection = (): void => {
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
};

const toggleView = (): void => {
  isCardView.value = !isCardView.value;
};

const filteredCollections = computed(() => {
  let collections = [...props.items];

  if (searchQuery.value) {
    const search = searchQuery.value.toLowerCase();
    collections = collections.filter(c => c.name.toLowerCase().includes(search));
  }

  collections.sort((a, b) => {
    let comparison = 0;
    const field = sortBy.value as keyof MemoCollection;

    if (field === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else if (field === 'created_at') {
      comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    } else if (field === 'memos_count') {
      comparison = (a.memos_count || 0) - (b.memos_count || 0);
    }
    
    return sortDirection.value === 'asc' ? comparison : -comparison;
  });

  return collections;
});

const paginatedCollections = computed(() => {
  const start = (currentPage.value - 1) * props.itemsPerPage;
  const end = start + props.itemsPerPage;
  return filteredCollections.value.slice(start, end);
});
</script>

<style scoped>
.anamnesis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.anamnesis-table {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style> 